import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';

import Button from 'shopper/components/Button';
import Icon from 'shopper/components/Icon';
import Tooltip from 'shopper/components/Tooltip';

import useAliasWishlist from 'hooks/useAliasWishlist';
import useModal from 'hooks/useModal';

import { sendEvent } from 'lib/gtag';
import placeholder from 'lib/placeholder';

import { useUser } from 'providers/UserProvider';

import Context from './Context';
import Like from './LikeButton';
import Share from './Share';

import OFFER from 'constants/offer';

const WishlistSuggestionsModal = dynamic(
  () => import('./WishlistSuggestionsModal'),
  { ssr: false }
);

/**
 * Offers with status WAITING are only accessible by the user
 * who posted it, shouldn't be able to share. FINISHED offers
 * shouldn't be shared for some reason too.
 */
const isOfferShareableInSocialMedia = (offerStatusName) =>
  offerStatusName !== OFFER.STATUSES.WAITING &&
  offerStatusName !== OFFER.STATUSES.FINISHED;

const getWishlistTooltipMessage = (aliasId, hasTerm) => {
  if (aliasId) {
    return hasTerm
      ? placeholder('actions.removeWishlistProductAlert')
      : placeholder('actions.receiveAlerts');
  }

  return hasTerm
    ? placeholder('actions.removeWishlistOfferAlert')
    : placeholder('actions.receiveAlertsOffers');
};

const Actions = ({
  aliasId,
  gaEventCategory,
  keywords,
  offerId,
  offerLikes,
  offerPrice,
  offerPriceType,
  offerStatusName,
  offerTitle,
  offerCoupon = null,
  offerInstructions,
  userId,
}) => {
  const { showModal } = useModal();
  const { runCallbackIfLoggedIn } = useUser();
  const [
    hasSuggestedKeywordAddedInWishlist,
    setHasSuggestedKeywordAddedInWishlist,
  ] = useState(false);
  const { addOfferWishlistAlias, removeOfferWishlistAlias, aliasWishlist } =
    useAliasWishlist(gaEventCategory, {
      gaEventLabel: 'offer_details',
    });

  const hasAliasTerm = useMemo(
    () => aliasWishlist?.items.some((item) => item.aliasId === aliasId),
    [aliasWishlist, aliasId]
  );

  const onHeartClick = () => {
    if (aliasId) {
      runCallbackIfLoggedIn(() => {
        if (hasAliasTerm) {
          removeOfferWishlistAlias({ aliasId });
          return;
        }

        addOfferWishlistAlias({ aliasId });
      });
      return;
    }

    sendEvent({
      action: 'show_wishlist_suggestions',
      category: gaEventCategory,
    });

    showModal(WishlistSuggestionsModal, {
      keywords,
      onAddKeyword: () => {
        setHasSuggestedKeywordAddedInWishlist(true);
      },
      onRemoveKeyword: () => {
        setHasSuggestedKeywordAddedInWishlist(false);
      },
    });
  };

  const hasTerm = hasAliasTerm || hasSuggestedKeywordAddedInWishlist;

  return (
    <div className="flex">
      <Tooltip.Root className="relative">
        <Tooltip.Trigger>
          <Button
            aria-label="Adicionar produto na lista de desejos"
            className="ml-auto [&>svg]:text-rd-primary-700"
            icon={<Icon name={hasTerm ? 'heart-filled' : 'heart'} />}
            size="size3"
            type="primary-ghost"
            onClick={onHeartClick}
          />
        </Tooltip.Trigger>
        <Tooltip.Box
          align="center"
          className="w-56 rounded-4 bg-rd-primary-100 px-8 text-center [&>span]:text-neutral-low-300"
          direction="top"
          size="size3"
        >
          {getWishlistTooltipMessage(aliasId, hasTerm)}
          <div className="absolute bottom-[-10px] left-1/2 size-0 -translate-x-1/2 border-[6px] border-b-0 border-t-[12px] border-solid border-transparent border-t-rd-primary-100 dark:border-t-neutral-high-100" />
        </Tooltip.Box>
      </Tooltip.Root>
      <div className="flex items-center justify-end gap-3">
        {isOfferShareableInSocialMedia(offerStatusName) && (
          <Share
            gaEventCategory={gaEventCategory}
            offerPrice={offerPrice}
            offerPriceType={offerPriceType}
            offerTitle={offerTitle}
          />
        )}
      </div>
      <Like
        gaEventCategory={gaEventCategory}
        likesCount={offerLikes}
        offerId={offerId}
        size="size3"
        type="primary"
        ghost
      />
      <Context
        gaEventCategory={gaEventCategory}
        offerCoupon={offerCoupon}
        offerId={offerId}
        offerInstructions={offerInstructions}
        offerPrice={offerPrice}
        offerPriceType={offerPriceType}
        offerStatusName={offerStatusName}
        userId={userId}
      />
    </div>
  );
};

Actions.propTypes = {
  aliasId: PropTypes.number,
  gaEventCategory: PropTypes.string.isRequired,
  keywords: PropTypes.arrayOf(PropTypes.string),
  offerCoupon: PropTypes.string,
  offerId: PropTypes.number.isRequired,
  offerInstructions: PropTypes.string.isRequired,
  offerLikes: PropTypes.number.isRequired,
  offerPrice: PropTypes.number.isRequired,
  offerPriceType: PropTypes.string.isRequired,
  offerStatusName: PropTypes.string.isRequired,
  offerTitle: PropTypes.string.isRequired,
  userId: PropTypes.number.isRequired,
};

export default Actions;
